// src/components/admin/ContactMessages.js
import React, { useState, useEffect, useCallback } from 'react';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const fetchMessages = useCallback(async (page) => {
    try {
      setLoading(true);
      let url = `/api/admin/contact-messages?page=${page}`;
      
      // Add category filter if not "all"
      if (categoryFilter !== 'all') {
        url += `&category=${categoryFilter}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      setMessages(data.messages);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Error fetching contact messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    fetchMessages(currentPage);
  }, [currentPage, categoryFilter, fetchMessages]); 


  const markAsRead = async (messageId, isRead) => {
    try {
      const response = await fetch(`/api/admin/contact-messages/${messageId}/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isRead }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to update message');
      }

      // Update the message in the list
      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId ? { ...msg, is_read: isRead } : msg
        )
      );
      
      // Also update the selected message if it's being viewed
      if (selectedMessage && selectedMessage.id === messageId) {
        setSelectedMessage(prev => ({...prev, is_read: isRead}));
      }
    } catch (err) {
      console.error('Error updating message:', err);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/admin/contact-messages/${messageId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete message');
      }

      // Remove the message from the list
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      
      // Close the modal if the deleted message was being viewed
      if (selectedMessage && selectedMessage.id === messageId) {
        setSelectedMessage(null);
      }
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'general':
        return 'bg-blue-900 text-blue-100';
      case 'support':
        return 'bg-green-900 text-green-100';
      case 'feedback':
        return 'bg-purple-900 text-purple-100';
      case 'bug':
        return 'bg-red-900 text-red-100';
      default:
        return 'bg-gray-700 text-gray-300';
    }
  };
  
  // Function to get a short excerpt of the message
  const getMessageExcerpt = (message, maxLength = 50) => {
    if (!message) return '';
    return message.length > maxLength 
      ? message.substring(0, maxLength) + '...' 
      : message;
  };
  
  // Function to open the message modal and mark as read
  const openMessageModal = async (message) => {
    setSelectedMessage(message);
    
    // If the message is unread, mark it as read
    if (!message.is_read) {
      await markAsRead(message.id, true);
    }
  };
  
  // Function to close the message modal
  const closeMessageModal = () => {
    setSelectedMessage(null);
  };


  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    
    try {
      setSendingReply(true);
      setError(null);
      
      const response = await fetch(`/api/admin/contact-messages/${selectedMessage.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ replyText }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send reply');
      }
      
      // Update the message in the list to show it's been responded to
      setMessages(prev => 
        prev.map(msg => 
          msg.id === selectedMessage.id ? { ...msg, responded: true } : msg
        )
      );
      
      // Close the reply form
      setShowReplyForm(false);
      setReplyText('');
      
      // Show a success message or update the UI as needed
      alert('Reply sent successfully!');
    } catch (err) {
      console.error('Error sending reply:', err);
      setError(err.message || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-blue-300">Contact Messages</h2>
        
        <div className="flex items-center">
          <label className="mr-2 text-gray-300">Filter by category:</label>
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setCurrentPage(1); // Reset to first page when filter changes
            }}
            className="bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Categories</option>
            <option value="general">General</option>
            <option value="email">Email</option>
            <option value="support">Support</option>
            <option value="feedback">Feedback</option>
            <option value="bug">Bug Report</option>
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
          <p className="text-gray-400 mt-2">Loading messages...</p>
        </div>
      ) : error ? (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-200">
          <p>{error}</p>
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p>No contact messages yet.</p>
        </div>
      ) : (
        <>
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Name</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Email</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Category</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Subject</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Message</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Date</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">User</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {messages.map(message => (
                    <tr 
                      key={message.id} 
                      className={`hover:bg-gray-750 ${!message.is_read ? 'bg-blue-900/10' : ''} cursor-pointer`}
                      onClick={() => openMessageModal(message)}
                    >
                      <td className="py-3 px-4 text-gray-300">{message.name}</td>
                      <td className="py-3 px-4 text-gray-300">{message.email}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${getCategoryColor(message.category)}`}>
                          {message.category || 'general'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-300">
                        {message.subject || 'No Subject'}
                      </td>
                      <td className="py-3 px-4 text-gray-300">
                        {getMessageExcerpt(message.message)}
                      </td>
                      <td className="py-3 px-4 text-gray-300">
                        {new Date(message.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' })}
                      </td>
                      <td className="py-3 px-4 text-gray-300">
                        {message.username ? message.username : 'Anonymous'}
                      </td>
                      <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(message.id, !message.is_read);
                            }}
                            className={`px-3 py-1 rounded-full text-xs ${
                              message.is_read
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {message.is_read ? 'Mark Unread' : 'Mark Read'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMessage(message.id);
                            }}
                            className="px-3 py-1 rounded-full text-xs bg-red-700 text-white hover:bg-red-800"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="flex justify-between items-center py-4 px-4 border-t border-gray-700">
                <div className="text-sm text-gray-400">
                  Showing page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage <= 1}
                    className={`px-3 py-1 rounded-md ${
                      currentPage <= 1
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage >= totalPages}
                    className={`px-3 py-1 rounded-md ${
                      currentPage >= totalPages
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Message Modal */}
          {selectedMessage && (
            <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 overflow-hidden">
              <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full border border-gray-700">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-white">Message from {selectedMessage.name}</h3>
                    <p className="text-sm text-gray-400 mt-1">
                      {new Date(selectedMessage.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button 
                    onClick={closeMessageModal}
                    className="text-gray-400 hover:text-white"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Modal Body */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-gray-400">From:</p>
                      <p className="text-gray-200">{selectedMessage.name} {selectedMessage.username && <span className="text-blue-400">({selectedMessage.username})</span>}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Email:</p>
                      <p className="text-gray-200">{selectedMessage.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Category:</p>
                      <p className="text-gray-200">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${getCategoryColor(selectedMessage.category)}`}>
                          {selectedMessage.category || 'general'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Status:</p>
                      <p className="text-gray-200">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                          selectedMessage.is_read
                            ? 'bg-gray-700 text-gray-300'
                            : 'bg-blue-600 text-white'
                        }`}>
                          {selectedMessage.is_read ? 'Read' : 'Unread'}
                        </span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="col-span-2">
                    <p className="text-sm text-gray-400">Subject:</p>
                    <p className="text-gray-200">{selectedMessage.subject || 'No Subject'}</p>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm text-gray-400 mb-2">Message:</p>
                    <div className="bg-gray-700 rounded-md p-4 text-gray-200 whitespace-pre-wrap">
                      {selectedMessage.message}
                    </div>
                  </div>
                </div>
                
                {/* Modal Footer */}
                <div className="px-6 py-4 border-t border-gray-700 flex justify-between">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => markAsRead(selectedMessage.id, !selectedMessage.is_read)}
                      className={`px-4 py-2 rounded-md ${
                        selectedMessage.is_read
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {selectedMessage.is_read ? 'Mark as Unread' : 'Mark as Read'}
                    </button>
                    <button
                      onClick={() => {
                        deleteMessage(selectedMessage.id);
                      }}
                      className="px-4 py-2 bg-red-700 text-white hover:bg-red-800 rounded-md"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setShowReplyForm(true)}
                      className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-md"
                    >
                      Reply
                    </button>
                  </div>
                  <button
                    onClick={closeMessageModal}
                    className="px-4 py-2 bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-md"
                  >
                    Close
                  </button>
                </div>

                {/* Reply Form Modal */}
                {selectedMessage && showReplyForm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-hidden">
                    <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full border border-gray-700">
                      <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
                        <h3 className="text-xl font-bold text-white">Reply to {selectedMessage.name}</h3>
                        <button 
                          onClick={() => setShowReplyForm(false)}
                          className="text-gray-400 hover:text-white"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      <div className="p-6">
                        <div className="mb-4">
                          <p className="text-sm text-gray-400 mb-1">To: {selectedMessage.name} &lt;{selectedMessage.email}&gt;</p>
                          <p className="text-sm text-gray-400 mb-3">
                            Subject: Re: {selectedMessage.subject || 'Your message to bestboard.games'}
                          </p>
                        </div>
                        
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          className="w-full p-3 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows="8"
                          placeholder="Type your reply here..."
                        ></textarea>
                        
                        {error && (
                          <div className="mt-4 bg-red-900/30 border border-red-700 p-3 rounded text-red-200">
                            {error}
                          </div>
                        )}
                      </div>
                      
                      <div className="px-6 py-4 border-t border-gray-700 flex justify-end space-x-2">
                        <button
                          onClick={() => setShowReplyForm(false)}
                          className="px-4 py-2 bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-md"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSendReply}
                          disabled={sendingReply || !replyText.trim()}
                          className={`px-4 py-2 rounded-md text-white ${
                            sendingReply || !replyText.trim() 
                              ? 'bg-green-700 cursor-not-allowed' 
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          {sendingReply ? 'Sending...' : 'Send Reply'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}


              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContactMessages;